import CustomInput from '../../../reusable/input';
import { DeleteOutlineRounded } from '@mui/icons-material';
import { reminderSchema } from '../../../../schema/reminder_schema';
import { timestampToFormattedDate } from '../../../../helper/index';
import { addDealReminderThunk, deleteDealReminderThunk } from '../../../../redux/thunks/reminder';
import { AppDispatch, RootState } from '../../../../redux/store';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: ReminderRender Component
 * @paragraph: This component provides the UI and logic for rendering and managing reminders associated with a deal.
 * Users can add, view, and delete reminders.
 * @interface: ReminderValues
 * @property: {string} name - The title of the reminder.
 * @property: {string} date - The due date for the reminder.
 * @property: {string} description - Details or description of the reminder.
 */
interface ReminderValues {
    name: string;
    date: string;
    time: string;
    description: string;
}

/**
 * @heading2: ReminderRender
 */
const ReminderRender = () => {
    /**
    * @heading3: Variables
    * @ol:
    * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
    * initialValues: {ReminderValues} - Set the initial values of the form.
    * selectedDeal/reminders: An object containing the details from the Redux store.
    */
    const dispatch = useDispatch<AppDispatch>();
    const { selectedDeal } = useSelector((state: RootState) => state.deal);
    const { loaders } = useSelector((state: RootState) => state.reminder);
    const { reminders } = useSelector((state: RootState) => state.reminder);
    const initialValues: ReminderValues = {
        name: '',
        date: '',
        description: '',
        time: '12:00'
    };
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const minDate = `${yyyy}-${mm}-${dd}`;

    /**
     * @heading3: Functions
     * @heading4: handleSubmit
     * @paragraph: Submits the reminder form data to the server to create a new reminder for the selected deal.
     * @param: {ReminderValues} values - The form values for the reminder.
     */
    const handleSubmit = async (values: ReminderValues) => {
        try {
            await dispatch(addDealReminderThunk({
                deal_id: selectedDeal?.id as number,
                title: values?.name,
                due_date: values.date,
                description: values?.description,
                due_time: values?.time
            })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    /**
     * @heading4: deleteReminderHandler
     * @paragraph: Deletes a specific reminder by its ID.
     * @param: {number} id - The ID of the reminder to delete.
     */
    const deleteReminderHandler = async (id: number) => {
        try {
            await dispatch(deleteDealReminderThunk({
                deal_id: selectedDeal?.id as number,
                reminder_id: id
            })).unwrap();
        } catch (e) {
            // Catch errors
        }
    };

    if (loaders.isLoading) {
        return <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" my={2}>
            <CircularProgress size={24} />
            <Typography ml={2}>Please wait...</Typography>
        </Box>;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={reminderSchema}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={1}>
                            {/* Input field for reminder title */}
                            <CustomInput
                                value={values.name}
                                onChange={handleChange('name')}
                                onBlur={handleBlur('name')}
                                error={touched.name && errors.name}
                                fullWidth
                                label="Name"
                                placeholder="Reminder Title"
                                variant="outlined"
                            />
                            {/* Input field for reminder date */}
                            <Box display="flex" gap={2} alignItems="center">
                                <CustomInput
                                    type="date"
                                    value={values.date}
                                    onChange={handleChange('date')}
                                    onBlur={handleBlur('date')}
                                    error={touched.date && errors.date}
                                    fullWidth
                                    label="Due Date"
                                    placeholder="2/22/2025"
                                    min={minDate}
                                    variant="outlined"
                                    mandatoryFields={true}
                                />
                                <CustomInput
                                    type="time"
                                    value={values.time}
                                    onChange={handleChange('time')}
                                    onBlur={handleBlur('time')}
                                    error={touched.time && errors.time}
                                    fullWidth
                                    label="Due Time"
                                    placeholder="12:00 AM"
                                    variant="outlined"
                                    mandatoryFields={true}
                                />
                            </Box>
                            {/* Input field for reminder description */}
                            <CustomInput
                                type="textarea"
                                rows={4}
                                value={values.description}
                                onChange={handleChange('description')}
                                onBlur={handleBlur('description')}
                                error={touched.description && errors.description}
                                fullWidth
                                label="Description"
                                placeholder="Description"
                                variant="outlined"
                                mandatoryFields={true}
                            />
                            {/* Action Button */}
                            <Button sx={{
                                marginTop: '10px'
                            }} type="submit" variant="outlined" color="primary" fullWidth disabled={loaders.isCreating || loaders.isDeleting}>
                                {loaders.isCreating ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </Form>)}
            </Formik>
            <Box maxHeight="250px" overflow="auto" mt={1}>
                {
                    reminders.map((reminder, index: number) => (
                        <Box key={index} minHeight="70px" p={1} mt={1} boxSizing="border-box" border="1px solid rgba(231,231,231,1)" borderRadius="5px">
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="rgba(51,56,63,1)" fontSize="14px" fontWeight="600">
                                    {reminder.title} {' '}
                                    <Typography component="span" fontSize="13px" fontWeight="400">
                                        {timestampToFormattedDate(reminder.created_at)}
                                    </Typography>
                                </Typography>
                                <IconButton onClick={() => deleteReminderHandler(reminder.id)}>
                                    <DeleteOutlineRounded fontSize="small" color="error" />
                                </IconButton>
                            </Box>
                            <Typography variant="body2" color="rgba(111,118,126,1)" fontSize="12px" fontWeight="400">
                                {reminder.description}
                            </Typography>
                        </Box>))
                }
            </Box>
        </Box>
    );
};

export default ReminderRender;